import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Filled,
  Dismiss24Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import { ClientSearchInput } from "components/ClientSearchInput";
import { ClientParametersDto } from "library/types/types";
import { ClientSearchResult } from "state/queries/useClientSearch";
import {
  EMPTY_PAYER_FORM,
  PayerFormData,
  getPayerDisplayName,
  payerFormFromGOS,
} from "./invoicePayer";

/** Selection key of the "different payer" choice (payer entered by hand or found in GOS) */
export const DIFFERENT_PAYER_KEY = "__differentPayer__";

export interface InvoicePayerOption {
  key: string;
  /** client of an offer, or payer of an already issued invoice */
  source: "client" | "payer";
  payer: ClientParametersDto;
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
`;

const SpanTwo = styled.div`
  grid-column: span 2;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  overflow-wrap: anywhere;
`;

const GOSIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.accent};
  background: ${({ theme }) => theme.palette.neutralLight || "#e6f4ea"};
  padding: 2px 4px 2px 10px;
  border-radius: 8px;
`;

const SearchDialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 500px;
  max-width: 600px;
  min-height: 300px;
`;

type Props = {
  /** Payers to choose from: the client of the offer (single invoice), or the clients and invoice payers of the selected offers (group invoice) */
  options: InvoicePayerOption[];
  selectedKey: string;
  onSelectedKeyChange: (key: string) => void;
  /** Data of the payer when DIFFERENT_PAYER_KEY is selected */
  differentPayer: PayerFormData;
  onDifferentPayerChange: (form: PayerFormData) => void;
  /** E-mail entered for a chosen client / payer that has none (the e-mail is mandatory for the invoice payer) */
  missingEmail: string;
  onMissingEmailChange: (email: string) => void;
  disabled?: boolean;
  /** In-app guide anchor (data-help) on the section root. */
  dataHelp?: string;
};

/**
 * Payer of an invoice (mandatory). With a single option (the client of the offer) the choice is
 * "Same as client" / "Different payer"; with several options they are offered in a dropdown together
 * with "Different payer". A different payer is searched in GOS or entered by hand.
 */
export const InvoicePayerSection = ({
  options,
  selectedKey,
  onSelectedKeyChange,
  differentPayer,
  onDifferentPayerChange,
  missingEmail,
  onMissingEmailChange,
  disabled = false,
  dataHelp,
}: Props) => {
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // payer code taken over from GOS: changing it means the payer no longer matches the GOS partner
  const gosCodeRef = useRef<string | null>(null);

  const isDifferent = selectedKey === DIFFERENT_PAYER_KEY;
  const selectedOption = options.find((o) => o.key === selectedKey) || null;
  const isCompany = differentPayer.payerType === "company";

  const optionLabel = (option: InvoicePayerOption): string => {
    if (options.length === 1 && option.source === "client") {
      return t("qtg.invoicePayer.sameAsClient", "Same as client");
    }
    const name = getPayerDisplayName(option.payer) || "-";
    const code = option.payer.Code ? ` (${option.payer.Code})` : "";
    const source =
      option.source === "client"
        ? t("qtg.invoicePayer.sourceClient", "client")
        : t("qtg.invoicePayer.sourcePayer", "payer");
    return `${name}${code} - ${source}`;
  };
  const differentPayerLabel = t(
    "qtg.invoicePayer.differentPayer",
    "Different payer",
  );

  const update = (changes: Partial<PayerFormData>) => {
    const next = { ...differentPayer, ...changes };
    if (
      changes.payerCode !== undefined &&
      next.isExistingInGOS &&
      gosCodeRef.current &&
      changes.payerCode !== gosCodeRef.current
    ) {
      next.isExistingInGOS = false;
      gosCodeRef.current = null;
    }
    onDifferentPayerChange(next);
  };

  const handleGOSSelect = (client: ClientSearchResult) => {
    const form = payerFormFromGOS(client);
    gosCodeRef.current = form.payerCode || null;
    onDifferentPayerChange(form);
    setIsSearchOpen(false);
  };

  const handleClearGOS = () => {
    gosCodeRef.current = null;
    onDifferentPayerChange(EMPTY_PAYER_FORM);
  };

  const textField = (
    field: keyof PayerFormData,
    labelKey: string,
    label: string,
    required = false,
    type: "text" | "email" | "tel" = "text",
  ) => (
    <Field label={t(labelKey, label)} required={required} size="small">
      <Input
        size="small"
        type={type}
        value={differentPayer[field] as string}
        onChange={(_, data) => update({ [field]: data.value })}
        disabled={disabled}
      />
    </Field>
  );

  return (
    <Section data-help={dataHelp}>
      <Header>
        <Title>{t("qtg.invoicePayer.title", "Payer")}</Title>
        {isDifferent && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {differentPayer.isExistingInGOS && (
              <GOSIndicator>
                <CheckmarkCircle20Filled />
                {t("qtg.proposal.payerData.existingInGOS", "Existing in GOS")}
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss24Regular />}
                  onClick={handleClearGOS}
                  disabled={disabled}
                  title={t(
                    "qtg.proposal.payerData.clearGOSPayer",
                    "Clear GOS payer",
                  )}
                  aria-label={t(
                    "qtg.proposal.payerData.clearGOSPayer",
                    "Clear GOS payer",
                  )}
                />
              </GOSIndicator>
            )}
            <Button
              appearance="secondary"
              size="small"
              icon={<Search20Regular />}
              onClick={() => setIsSearchOpen(true)}
              disabled={disabled}
            >
              {t("qtg.proposal.payerData.searchPayer", "Search payer")}
            </Button>
          </div>
        )}
      </Header>

      {options.length <= 1 ? (
        <RadioGroup
          layout="horizontal"
          value={selectedKey}
          onChange={(_, data) => onSelectedKeyChange(data.value)}
          disabled={disabled}
        >
          {options.map((o) => (
            <Radio key={o.key} value={o.key} label={optionLabel(o)} />
          ))}
          <Radio value={DIFFERENT_PAYER_KEY} label={differentPayerLabel} />
        </RadioGroup>
      ) : (
        <Field
          label={t("qtg.invoicePayer.select", "Invoice addressed to")}
          required
          size="small"
        >
          <Dropdown
            size="small"
            placeholder={t("qtg.invoicePayer.selectPlaceholder", "Select payer")}
            value={
              selectedOption
                ? optionLabel(selectedOption)
                : isDifferent
                  ? differentPayerLabel
                  : ""
            }
            selectedOptions={selectedKey ? [selectedKey] : []}
            onOptionSelect={(_, data) =>
              data.optionValue && onSelectedKeyChange(data.optionValue)
            }
            disabled={disabled}
          >
            {options.map((o) => (
              <Option key={o.key} value={o.key}>
                {optionLabel(o)}
              </Option>
            ))}
            <Option value={DIFFERENT_PAYER_KEY}>{differentPayerLabel}</Option>
          </Dropdown>
        </Field>
      )}

      {selectedOption && <PayerSummary payer={selectedOption.payer} />}

      {selectedOption && !selectedOption.payer.Address?.Email?.trim() && (
        <Field
          label={t("qtg.proposal.payerData.payerEmail", "Payer e-mail")}
          hint={t(
            "qtg.invoicePayer.missingEmailHint",
            "No e-mail is stored for this payer; it is used for this invoice only",
          )}
          required
          size="small"
        >
          <Input
            size="small"
            type="email"
            value={missingEmail}
            onChange={(_, data) => onMissingEmailChange(data.value)}
            disabled={disabled}
          />
        </Field>
      )}

      {isDifferent && (
        <Grid>
          <Field
            label={t("qtg.proposal.payerData.payerType", "Payer type")}
            required
            size="small"
          >
            <Dropdown
              size="small"
              value={
                isCompany
                  ? t("qtg.proposal.clientData.options.company", "Company")
                  : t("qtg.proposal.clientData.options.person", "Person")
              }
              selectedOptions={[differentPayer.payerType]}
              onOptionSelect={(_, data) =>
                data.optionValue &&
                update({
                  payerType: data.optionValue as PayerFormData["payerType"],
                })
              }
              disabled={disabled}
            >
              <Option value="company">
                {t("qtg.proposal.clientData.options.company", "Company")}
              </Option>
              <Option value="person">
                {t("qtg.proposal.clientData.options.person", "Person")}
              </Option>
            </Dropdown>
          </Field>
          {textField(
            "payerCode",
            "qtg.proposal.payerData.payerCode",
            "Payer code",
            true,
          )}
          {isCompany ? (
            <SpanTwo>
              {textField(
                "payerName",
                "qtg.proposal.payerData.payerName",
                "Payer name",
                true,
              )}
            </SpanTwo>
          ) : (
            <>
              {textField(
                "payerFirstName",
                "qtg.proposal.payerData.payerFirstName",
                "First name",
                true,
              )}
              {textField(
                "payerLastName",
                "qtg.proposal.payerData.payerLastName",
                "Last name",
                true,
              )}
            </>
          )}
          <SpanTwo>
            {textField(
              "address",
              "qtg.proposal.payerData.address",
              "Address",
              true,
            )}
          </SpanTwo>
          {textField("city", "qtg.proposal.payerData.city", "City")}
          {textField(
            "postIndex",
            "qtg.proposal.payerData.postIndex",
            "Post index",
          )}
          {textField(
            "payerEmail",
            "qtg.proposal.payerData.payerEmail",
            "Payer e-mail",
            true,
            "email",
          )}
          {textField(
            "payerPhoneNumber",
            "qtg.proposal.payerData.payerPhoneNumber",
            "Payer phone number",
            false,
            "tel",
          )}
          <Checkbox
            label={t("qtg.proposal.payerData.resident", "Resident")}
            checked={differentPayer.resident}
            onChange={(_, data) => update({ resident: !!data.checked })}
            disabled={disabled}
          />
        </Grid>
      )}

      <Dialog
        modalType="modal"
        open={isSearchOpen}
        onOpenChange={(_, data) => setIsSearchOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label={t("qtg.validation.close", "Close")}
                  icon={<Dismiss24Regular />}
                  onClick={() => setIsSearchOpen(false)}
                />
              }
            >
              {t("qtg.proposal.payerData.gosPayerSearch", "GOS payer search")}
            </DialogTitle>
            <DialogContent>
              <SearchDialogContent>
                <ClientSearchInput onClientSelect={handleGOSSelect} />
              </SearchDialogContent>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </Section>
  );
};

/** Read-only data of a payer chosen from the offered clients / payers */
const PayerSummary = ({ payer }: { payer: ClientParametersDto }) => {
  const { t } = useTranslation();
  const address = [payer.Address?.AddressString, payer.Address?.City]
    .filter(Boolean)
    .join(", ");
  const item = (label: string, value: string | null | undefined) => (
    <div>
      <SummaryLabel>{label}</SummaryLabel>
      <SummaryValue>{value || "-"}</SummaryValue>
    </div>
  );
  return (
    <Grid>
      {item(
        payer.IsCompany
          ? t("qtg.proposal.payerData.payerName", "Payer name")
          : `${t("qtg.proposal.payerData.payerFirstName", "First name")} / ${t(
              "qtg.proposal.payerData.payerLastName",
              "Last name",
            )}`,
        getPayerDisplayName(payer),
      )}
      {item(t("qtg.proposal.payerData.payerCode", "Payer code"), payer.Code)}
      <SpanTwo>
        {item(t("qtg.proposal.payerData.address", "Address"), address)}
      </SpanTwo>
      {item(
        t("qtg.proposal.payerData.payerEmail", "Payer e-mail"),
        payer.Address?.Email,
      )}
      {item(
        t("qtg.proposal.payerData.payerPhoneNumber", "Payer phone number"),
        payer.Address?.Phone || payer.Address?.MobilePhone,
      )}
    </Grid>
  );
};
